import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type FocusEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../lib/cn';

export type TooltipSide = 'top' | 'bottom' | 'left' | 'right';
export type TooltipSize = 'sm' | 'md' | 'lg';

export type TooltipProps = {
  content: ReactNode;
  children: ReactNode;
  side?: TooltipSide;
  className?: string;
  /**
   * When true, allow multi-line help text (field tooltips). Default is
   * single-line for icon-only progressive disclosure.
   */
  multiline?: boolean;
  /** Width ladder aligned with former product Tooltip sizes. */
  size?: TooltipSize;
  /**
   * `fixed` (default) portals to `document.body` with viewport coordinates from
   * `getBoundingClientRect()` so overflow-clipped or transformed ancestors do
   * not crop or re-root the panel, and opening never expands page/table scroll
   * height. Use `absolute` only for simple chrome that is not inside
   * overflow:auto containers.
   */
  strategy?: 'absolute' | 'fixed';
  /**
   * Allow pointer events on the panel (links). Default false keeps the panel
   * non-interactive so it never steals hover from the trigger. Prefer short
   * non-interactive copy; use only when content must contain anchors.
   * Pair with a focusable trigger (e.g. button) so keyboard users can open it.
   */
  interactive?: boolean;
};

const sideClassAbsolute: Record<TooltipSide, string> = {
  top: 'bottom-full left-1/2 mb-1.5 -translate-x-1/2',
  bottom: 'top-full left-1/2 mt-1.5 -translate-x-1/2',
  left: 'right-full top-1/2 mr-1.5 -translate-y-1/2',
  right: 'left-full top-1/2 ml-1.5 -translate-y-1/2',
};

const sizeClass: Record<TooltipSize, string> = {
  sm: 'min-w-44 max-w-xs',
  md: 'min-w-56 max-w-sm',
  lg: 'min-w-72 max-w-md',
};

/** Gap used when bridging hover/focus from trigger → interactive panel. */
const INTERACTIVE_CLOSE_MS = 120;

function fixedStyle(
  rect: DOMRect,
  side: TooltipSide,
  gap = 6
): CSSProperties {
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  switch (side) {
    case 'bottom':
      return {
        position: 'fixed',
        top: rect.bottom + gap,
        left: cx,
        transform: 'translateX(-50%)',
      };
    case 'left':
      return {
        position: 'fixed',
        top: cy,
        left: rect.left - gap,
        transform: 'translate(-100%, -50%)',
      };
    case 'right':
      return {
        position: 'fixed',
        top: cy,
        left: rect.right + gap,
        transform: 'translateY(-50%)',
      };
    case 'top':
    default:
      return {
        position: 'fixed',
        top: rect.top - gap,
        left: cx,
        transform: 'translate(-50%, -100%)',
      };
  }
}

/**
 * Lightweight hover/focus tooltip. Prefer for icon-only controls and field help.
 * Keep copy short — tooltips are progressive disclosure, not help docs.
 *
 * Accessibility: tooltip panel has a stable `id`; the trigger is wrapped with
 * `aria-describedby` so screen readers announce help text (not only the label).
 *
 * Interactive panels (`interactive`) are an exception for link-bearing help;
 * prefer a Popover/Dialog for heavier UI. Always give interactive tips a
 * focusable trigger (button) so keyboard users can open the panel.
 */
export function Tooltip({
  content,
  children,
  side = 'top',
  className,
  multiline = false,
  size = 'md',
  strategy = 'fixed',
  interactive = false,
}: TooltipProps): ReactElement {
  const tooltipId = useId();
  const rootRef = useRef<HTMLSpanElement>(null);
  const panelRef = useRef<HTMLSpanElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [open, setOpen] = useState(false);
  const [fixedCss, setFixedCss] = useState<CSSProperties | undefined>(undefined);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current != null) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

  const updateFixedPosition = useCallback(() => {
    if (strategy !== 'fixed' || !rootRef.current) return;
    const rect = rootRef.current.getBoundingClientRect();
    setFixedCss(fixedStyle(rect, side));
  }, [side, strategy]);

  const show = useCallback(() => {
    clearCloseTimer();
    // Position before paint when possible so the first open frame is out-of-flow.
    if (strategy === 'fixed') updateFixedPosition();
    setOpen(true);
  }, [clearCloseTimer, strategy, updateFixedPosition]);

  const hide = useCallback(() => {
    if (!interactive) {
      setOpen(false);
      return;
    }
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setOpen(false);
      closeTimerRef.current = null;
    }, INTERACTIVE_CLOSE_MS);
  }, [clearCloseTimer, interactive]);

  /** True if focus/hover target is the trigger root or the (possibly portaled) panel. */
  const isWithinTooltip = useCallback((node: Node | null): boolean => {
    if (!node) return false;
    return Boolean(
      rootRef.current?.contains(node) || panelRef.current?.contains(node)
    );
  }, []);

  const onRootBlur = useCallback(
    (e: FocusEvent<HTMLSpanElement>) => {
      if (!isWithinTooltip(e.relatedTarget)) {
        hide();
      }
    },
    [hide, isWithinTooltip]
  );

  const onPanelBlur = useCallback(
    (e: FocusEvent<HTMLSpanElement>) => {
      if (!isWithinTooltip(e.relatedTarget)) {
        hide();
      }
    },
    [hide, isWithinTooltip]
  );

  useLayoutEffect(() => {
    if (!open || strategy !== 'fixed') return undefined;
    updateFixedPosition();
    const onScrollOrResize = (): void => updateFixedPosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, strategy, updateFixedPosition]);

  // Portal fixed panels to body so viewport coords stay correct under transformed
  // ancestors. Interactive hover/focus is bridged via panelRef.
  const usePortal = strategy === 'fixed' && typeof document !== 'undefined';

  // Only mount when open: a closed panel still in the tree (even absolute/fixed
  // with opacity-0) can expand ancestor overflow scrollHeight and shift layout.
  // Fixed without coords applied was especially bad — in-flow under document.body.
  const panelClass = cn(
    'z-50 rounded-ds-md bg-ds-text px-2 py-1 text-ds-xs text-ds-inverse shadow-ds-sm',
    interactive ? 'pointer-events-auto' : 'pointer-events-none',
    multiline ? 'whitespace-normal text-left leading-snug' : 'whitespace-nowrap',
    sizeClass[size],
    strategy === 'absolute' && 'absolute',
    strategy === 'absolute' && sideClassAbsolute[side]
  );

  // Fixed strategy must always carry position:fixed in the style object even
  // before measure completes — never leave a portaled panel in normal flow.
  const panelStyle: CSSProperties | undefined =
    strategy === 'fixed'
      ? {
          position: 'fixed',
          ...(fixedCss ?? {
            top: 0,
            left: 0,
            // Off-screen until measured; visibility alone is not enough for a11y tree.
            transform: 'translate(-100vw, -100vh)',
          }),
        }
      : undefined;

  const panel = open ? (
    <span
      ref={panelRef}
      id={tooltipId}
      role="tooltip"
      className={panelClass}
      style={panelStyle}
      onMouseEnter={interactive ? show : undefined}
      onMouseLeave={interactive ? hide : undefined}
      onFocusCapture={interactive ? show : undefined}
      onBlurCapture={interactive ? onPanelBlur : undefined}
    >
      {content}
    </span>
  ) : null;

  return (
    <span
      ref={rootRef}
      className={cn('relative inline-flex', className)}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocusCapture={show}
      onBlurCapture={onRootBlur}
    >
      {/*
        aria-describedby links the focusable child (e.g. Info control) to the
        help panel so SRs announce the description, not only the trigger name.
        Id is stable; the panel mounts while open/focused.
      */}
      <span
        className="inline-flex"
        {...(open ? { 'aria-describedby': tooltipId } : {})}
      >
        {children}
      </span>
      {panel != null
        ? usePortal
          ? createPortal(panel, document.body)
          : panel
        : null}
    </span>
  );
}
